
import * as Inovx from '@shared';
import assign from 'object-assign';
import { apiPostJson, apiRequest } from '@shared/api/apiClient';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var DateTimeOffsetHelper = Inovx.Shared.Common.DateTimeOffsetHelper;
var SystemActions = Inovx.Shared.System.Actions;
var url = `${UrlUtilities.getApiUrl()}/FacilityApi/api/KnowledgeBook`;

function isClassificationTagsInvalidValue(error: unknown, service: IServiceObject): boolean {
    const messageObject = service.parseError(error as IAjaxErrorObject);
    return messageObject?.reason === 'InvalidValue'
        && messageObject?.propertyName === 'ClassificationTags';
}

function isClassificationTagsEmptyValue(error: unknown, service: IServiceObject): boolean {
    const messageObject = service.parseError(error as IAjaxErrorObject);
    return messageObject?.reason === 'EmptyValue'
        && messageObject?.propertyName === 'ClassificationTags';
}

var KnowledgeBookServices: IKnowledgeBookServices = assign({}, Inovx.Shared.Flux.BaseService, {    
    getFacilityKnowledgeBooks: function (facilityID: string) {
        return new Promise<IKnowledgeBook[]>((resolve, reject) => {
            this.get(`${url}/ByFacility`,
                { facilityID: facilityID },
                (retData: IKnowledgeBook[]) => {
                    resolve(retData)
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },

    getKnowledgeBookData: function (knowledgeBookID: string) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return new Promise<IKnowledgeBookData>((resolve, reject) => {
            this.get(`${url}/Data`,
                { knowledgeBookID: knowledgeBookID, clientTime: clientTime },
                (retData: IKnowledgeBookData) => {
                    resolve(retData)
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    getKnowledgeBook: function (id: string) {
        return new Promise<IKnowledgeBook>((resolve, reject) => {
            this.get(`${url}/ById`,
                { id },
                (retData: IKnowledgeBook) => {
                    resolve(retData)
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    getFavoriteKnowledgeBookIds: function () {
        return new Promise<string[]>((resolve, reject) => {
            this.get(`${url}/Favorites/Ids`,
                null,
                (retData: string[]) => {
                    resolve(retData)
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    addFavoriteKnowledgeBook: function (knowledgeBookID: string) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return new Promise<void>((resolve, reject) => {
            this.post(`${url}/Favorites?knowledgeBookID=${encodeURIComponent(knowledgeBookID)}&clientTime=${encodeURIComponent(clientTime)}`,
                {},
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    removeFavoriteKnowledgeBook: function (knowledgeBookID: string) {
        return apiRequest<void>({
            url: `${url}/Favorites`,
            method: 'DELETE',
            query: { knowledgeBookID },
            responseType: 'none',
        }).catch((error: any) => {
            this.handleWebApiError(error);
            throw error;
        });
    },
    batchUpdateFavoriteKnowledgeBooks: function (knowledgeBookIDs: string[], add: boolean) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return apiPostJson<void>(`${url}/Favorites/Batch?clientTime=${encodeURIComponent(clientTime)}`, { KnowledgeBookIDs: knowledgeBookIDs, Add: add })
            .catch((error: IServiceErrorObject) => {
                this.handleWebApiError(error);
                throw error;
            });
    },
    batchDeleteKnowledgeBooks: function (knowledgeBookIDs: string[]) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return apiPostJson<IBatchKnowledgeBookDeleteResult>(`${url}/Delete/Batch?clientTime=${encodeURIComponent(clientTime)}`, { KnowledgeBookIDs: knowledgeBookIDs })
            .catch((error: IServiceErrorObject) => {
                this.handleWebApiError(error);
                throw error;
            });
    },
    createKnowledgeBook: function (kb: IKnowledgeBook) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return new Promise<IKnowledgeBook>((resolve, reject) => {
            this.post(`${url}/Create?clientTime=${encodeURIComponent(clientTime)}`, kb,
                (data: any) => {
                    resolve(data);
                },
                (error: any) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },

    editKnowledgeBook: function (kb: IKnowledgeBook) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return new Promise<IKnowledgeBook>((resolve, reject) => {
            this.post(`${url}/Update?clientTime=${encodeURIComponent(clientTime)}`, kb,
                (data: any) => {
                    resolve(data);
                },
                (error: any) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });    
    },
    batchUpdateClassificationTags: function (knowledgeBookIDs: string[], classificationTagIDs: string[], replaceExisting: boolean, knowledgeBookVersionTokens: Record<string, string>) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return apiPostJson<void>(`${url}/ClassificationTags/Batch?clientTime=${encodeURIComponent(clientTime)}`, {
            KnowledgeBookIDs: knowledgeBookIDs,
            KnowledgeBookVersionTokens: knowledgeBookVersionTokens,
            ClassificationTagIDs: classificationTagIDs,
            ReplaceExisting: replaceExisting,
        })
            .catch((error: IServiceErrorObject) => {
                if (isClassificationTagsEmptyValue(error, this)) {
                    SystemActions.displayErrorMessage({
                        message: InovxLoader.t('facility:knowledgeBook.classificationTagsRequired'),
                    });
                } else if (isClassificationTagsInvalidValue(error, this)) {
                    SystemActions.displayErrorMessage({
                        message: InovxLoader.t('facility:knowledgeBook.assignClassificationTagsHierarchyError'),
                    });
                } else {
                    this.handleWebApiError(error);
                }
                throw error;
            });
    },
    deleteKnowledgeBook: function (kb: IKnowledgeBook) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return new Promise<void>((resolve, reject) => {
            this.post(`${url}/Delete?clientTime=${encodeURIComponent(clientTime)}`,
                { ID: kb.ID, VersionToken: kb.VersionToken },
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    setKnowledgeViewOrder: function (kb: IKnowledgeBook, kvList: string[]) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return new Promise<void>((resolve, reject) => {
            this.post(`${url}/SetOrder?clientTime=${encodeURIComponent(clientTime)}`,
                { id: kb.ID, versionToken: kb.VersionToken, kvList: kvList },
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    setKnowledgeView3DOrder: function (kb: IKnowledgeBook, kvList: string[]) {
        return new Promise<void>((resolve, reject) => {
            this.post(`${url}/Set3DOrder`,
                { id: kb.ID, versionToken: kb.VersionToken, kvList: kvList },
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
});

export default KnowledgeBookServices;
